import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { HttpService } from './http.service';
import { CachingService } from './caching.service';
import { LanguageHandlerService } from './language-handler.service';
import { environment } from 'src/environments/environment';
import {
  getAllServicesParams,
  getAppKeyBody,
  getAppLangBody,
  getAuthHeaders,
  getBannerParams,
  getDeptServicesBody,
  getEncDecBody,
  getHomeServicesParams,
  getImportantUpdatesHeaders,
} from '../helpers/http-helpers';
import { cacheKeys } from '../constants/cache-keys';
import { BannerResp } from '../models/banner.model';
import {
  AllStatisticsResp
} from '../models/statistics.model';
import { CategoriesResp } from '../models/categories-model';
import { PopularService } from '../models/popular-services.model';
import { TrendingServiceResp } from '../models/trending-services.model';
import { WhatsNewServiceResp } from '../models/whats-new-services.model';
import {
  AllServicesResp,
  DeptServiceBody,
  DeptServiceResp
} from '../models/all-services.model';
import {
  EmailSubscribeBody,
  EmailSubscribeResp,
} from '../models/newsletter.model';
import { PartnerOnboard } from '../models/partner-onboard';
import { SearchLang } from '../models/search-lang.model';
import { AppSearchKey } from '../models/app-key-search.model';
import { DBTScheme, UmangStats, UserStats } from '../models/umang-stats.model';

@Injectable({
  providedIn: 'root',
})
export class CallAPIService {
  private searchEndpoint:string = 'search/core/api/search/v2/'
  private userProfileUrl = 'assets/json/user-profile.json';
  private searchSuggestionEndpoint = 'search/core/api/search/v1/suggestions';

  constructor(
    private langHandlerService: LanguageHandlerService,
    private cachingService: CachingService,
    private httpService: HttpService,
  ) {}

  private checkAPIResp(respCode: string, dataName: string, toCheckCode = 'su') {
    const isAPIRespSuccess = respCode?.toLowerCase() === toCheckCode;

    if (isAPIRespSuccess) {
      return;
    }

    // this.matDialog.open(ErrorDialogComponent);
    throw new Error(`Error in fetching ${dataName}`);
  }

  fetchBannerImages() {
    return this.httpService
      .sendGetRequest<BannerResp>({
        url: `${environment.CORE_CACHE_API_URL}/fetchbannersv5`,
        headers: getAuthHeaders(),
        params: getBannerParams(
          this.langHandlerService.currentLang,
          'web',
          '2048'
        ),
      })
      .pipe(
        map(({ rs, pd: { listHeroSpace } }) => {
          this.checkAPIResp(rs, 'banner images');
          this.cachingService.storeCachedData({
            key: cacheKeys.banners,
            data: listHeroSpace,
          });
          return listHeroSpace;
        }),
        catchError((error) => of(null))
      );
  }

  fetchStatsRecord() {
    const statsRecordParams = new HttpParams().append(
      'api_key',
      environment.resultsJSONApiKey
    );
    // ?dateStr=${formatCurrentDate()}
    return this.httpService
      .sendGetRequest<AllStatisticsResp>({
        url: `${
          environment.CORE_CACHE_API_URL
        }/landingstats`,
        // params: statsRecordParams,
        headers: getAuthHeaders(),
      })
      .pipe(
        map(({ rs, pd: result }) => {
          this.checkAPIResp(rs, 'service statistics', 's');

          const allStatistics = {
            ...result,
            otherServices: String(
              +result.stateServiceCount + +result.centerServiceCount
            ),
          };

          this.cachingService.storeCachedData({
            key: cacheKeys.statistics,
            data: allStatistics,
            isLangDep: false,
          });
          return allStatistics;
        }),
        catchError((error) =>
          of({
            totalTransactionCount: '0',
            totalRegisteredUser: '0',
            billPayments: '0',
            stateDeptCount: '0',
            centerDeptCount: '0',
            otherServices: '0',
            totalServie: '0',
            totalDept: '0',
          })
        )
      );
  }

  fetchWhatsNewSrvs() {
    return this.httpService
      .sendGetRequest<WhatsNewServiceResp>({
        url: `${environment.CORE_CACHE_API_URL}/newandupdatedservicecardv2`,
        params: getHomeServicesParams(this.langHandlerService.currentLang, '2'),
        headers: getAuthHeaders(),
      })
      .pipe(
        map(({ rs, pd: { serviceCardList } }) => {
          this.checkAPIResp(rs, "what's new services");
          this.cachingService.storeCachedData({
            key: cacheKeys.whatsNewSrvs,
            data: serviceCardList,
          });
          return serviceCardList;
        }),
        catchError((error) => of(null))
      );
  }

  fetchPopularServices() {
    return this.httpService
      .sendGetRequest<PopularService[]>({
        url: `${environment.frontEndUrl}service/v1/service-categories?category=popular&lang=${this.langHandlerService.currentLang || 'en'}`,
        headers: getAuthHeaders(true),
      })
      .pipe(
        tap({
          next: (popularSrvs) => {
            this.cachingService.storeCachedData({
              key: cacheKeys.popularSrvs,
              data: popularSrvs,
            });
          },
        }),
        catchError((error) => of(null))
      );
  }

  fetchPartnersOnboard() {
    return this.httpService
      .sendGetRequest<PartnerOnboard[]>({
        url: `${environment.CORE_API_URL}/getpartners`,
        // params: getBannerParams(this.langHandlerService.currentLang, 'app'),
        headers: getAuthHeaders(),
      })
      .pipe(
        tap({
          next: (getpartners) => {
            // this.cachingService.storeCachedData({
            //   key: cacheKeys.getpartners,
            //   data: getpartners,
            // });
          },
        }),
        catchError((error) => of(null))
      );
  }

  fetchTrendingServices() {
    return this.httpService
      .sendGetRequest<TrendingServiceResp>({
        url: `${environment.CORE_CACHE_API_URL}/trendingservicecardv2`,
        params: getHomeServicesParams(this.langHandlerService.currentLang, '3'),
        headers: getAuthHeaders(),
      })
      .pipe(
        map(({ rs, pd: { serviceCardList } }) => {
          this.checkAPIResp(rs, 'trending services');
          this.cachingService.storeCachedData({
            key: cacheKeys.trendingSrvs,
            data: serviceCardList,
          });
          return serviceCardList;
        }),
        catchError((error) => of(null))
      );
  }

  fetchCategories() {
    const fetchCategoryParams = new HttpParams().append(
      'lang',
      this.langHandlerService.currentLang
    );

    return this.httpService
      .sendGetRequest<CategoriesResp>({
        url: `${environment.CORE_CACHE_API_URL}/fetchcategory`,
        params: fetchCategoryParams,
        headers: getAuthHeaders(),
      })
      .pipe(
        map(({ rs, pd: { primaryCategoryList } }) => {
          this.checkAPIResp(rs, 'categories');
          this.cachingService.storeCachedData({
            key: cacheKeys.categories,
            data: primaryCategoryList,
          });
          return primaryCategoryList;
        }),
        catchError((error) => of(null))
      );
  }

  fetchAllServicesFromAPI() {
    return this.httpService
      .sendGetRequest<AllServicesResp>({
        url: `${environment.CORE_CACHE_API_URL}/allservicesv2`,
        params: getAllServicesParams(this.langHandlerService.currentLang),
        headers: getAuthHeaders(),
      })
      .pipe(
        map(({ rs, pd: { addServiceList } }) => {
          this.checkAPIResp(rs, 'all services');
          this.cachingService.storeCachedData({
            key: cacheKeys.allServices,
            data: addServiceList,
          });
          return addServiceList;
        }),
        catchError((error) => of(null))
      );
  }

  fetchDeptServices(serviceId: string) {
    return this.httpService
      .sendPostRequest<DeptServiceResp, DeptServiceBody>({
        url: environment.DEPT_SERVICE_URL,
        headers: new HttpHeaders(
          environment.production
            ? { 'x-api-key': environment.xApiKey }
            : {'x-api-key': environment.xApiKey }
        ),
        body: getDeptServicesBody(
          serviceId,
          this.langHandlerService.currentLang
        ),
      })
      .pipe(
        map(({ rs, pd: { services } }) => {
          this.checkAPIResp(rs, 'dept.-services');
          return services;
        }),
        catchError((error) => of(null))
      );
  }

  subscribeToNewsletter(emailId: string) {
    return this.httpService.sendPostRequest<
      EmailSubscribeResp,
      EmailSubscribeBody
    >({
      url: `${
        environment[
          environment.production ? 'CORE_API_URL' : 'OLD_CORE_API_URL'
        ]
      }/subscribe`,
      headers: environment.production
        ? getAuthHeaders()
        : new HttpHeaders({
            Authorization: environment.stagAuthToken,
          }),
      body: {
        email: emailId,
        mod: 'web',
      },
    });
  }

  public appLangSearch(
    searchKeyword: any,
    catid?: any,
    st?: any,
    isStateNegative?: any
  ) {
    const reqBody = {
      payload: getEncDecBody(
        getAppLangBody(searchKeyword, catid, st, isStateNegative, this.langHandlerService.currentLang),
        'https://apigw.umangapp.in/umangApiShard/ws1/'
      ),
    };

    return this.httpService
      .sendPostRequest<SearchLang, typeof reqBody>({
        url: environment.frontApiUrl + 'xcommonencryptrequest/' + 'applangsrch',
        body: reqBody,
      })
      .pipe(map(({ data: { pd } }) => pd));
  }

  public appKeySearch(
    searchKeyword: any,
    catid?: any,
    st?: any,
    isStateNegative?: any
  ) {
    const reqBody = {
      payload: getEncDecBody(
        getAppKeyBody(searchKeyword, this.langHandlerService.currentLang),
        'https://apigw.umangapp.in/umangApiShard/ws1/'
      ),
    };

    return this.httpService
      .sendPostRequest<AppSearchKey, typeof reqBody>({
        url:
          environment.frontApiUrl + 'xcommonencryptrequest/' + 'appkeysearch',
        body: reqBody,
      })
      .pipe(map(({ data: { pd } }) => pd));
  }
  fetchSchemeData() {
    return this.httpService
      .sendGetRequest<any>({
        url: 'https://web.umang.gov.in/frontapi/api-config/scheme/list'
      })
      .pipe(
        map((result) => {
          this.cachingService.storeCachedData({
            key: cacheKeys.schemeApplyData,
            data: result,
          });
          return result;
        }),
        catchError((error) => of(null))
      );
  }
  
  public callSearchAPI(url:any, payload?:any, header?:any) {
    return this.httpService.callNssoGetApi(this.searchEndpoint+url, payload, header);
  }



  /**
   * Method that is used to fetch dummy user profile details.
   * @returns Observable that handles fetching user profile request.
   */
  fetchUserProfile() {
    const isLocalEnv = window.location.host.includes('localhost');

    if (!isLocalEnv) {
      // User profile for not authenticated user.
      return of({
        tkn: '',
        lang: '',
        macAddress: '',
        userinfo: {
          uid: '',
          nam: '',
          gndr: '',
          mno: '',
          email: '',
          addr: '',
        },
      });
    }

    const cachedUserProfile =
      this.cachingService.fetchCachedData<any>('dummy-user-profile');

    if (cachedUserProfile) {
      return of(cachedUserProfile);
    }

    return this.httpService
      .sendGetRequest<any>({
        url: this.userProfileUrl,
      })
      .pipe(
        tap({
          next: (userProfile) => {
            this.cachingService.storeCachedData({
              key: 'dummy-user-profile',
              data: userProfile,
            });
          },
        })
      );
  }

  public callSearchSuggestionAPI(url:any, payload?:any, header?:any) {
    return this.httpService.callNssoGetApi(
      this.searchSuggestionEndpoint + url,
      payload,
      header
    );
  }
  getUmangStats():Observable<UmangStats>{
    return this.httpService.sendGetRequest({
      url:`${environment.API_BASE_URL}/publicdashboard/umangstats`,
      headers:getAuthHeaders(),
    })
  }

  getUserStats():Observable<UserStats>{
    return this.httpService.sendGetRequest({
      url:`${environment.API_BASE_URL}/publicdashboard/userstats`,
      headers:getAuthHeaders(),
    })
  }

  getTransactionStats():Observable<UserStats>{
    return this.httpService.sendGetRequest({
      url:`${environment.API_BASE_URL}/publicdashboard/monthwisestats`,
      headers:getAuthHeaders(),
    })
  }

  getTopDepartment(){
    return this.httpService.sendGetRequest({
      url:`${environment.API_BASE_URL}/publicdashboard/toptwentydept`,
      headers:getAuthHeaders(),
    })
  }

  getTopServices(){
    return this.httpService.sendGetRequest({
      url:`${environment.API_BASE_URL}/publicdashboard/toptwentyservice`,
      headers:getAuthHeaders(),
    })
  }

  getTopDeptServices(){
    return this.httpService.sendGetRequest({
      url:`${environment.API_BASE_URL}/publicdashboard/top20DeptServList`,
      headers:getAuthHeaders(),
    })
  }
  
  getServiceData(){
    return this.httpService.sendGetRequest({
      url:`${environment.API_BASE_URL}/publicdashboard/servicecountmonthwise`,
      headers:getAuthHeaders(),
    })
  }

  getCount(){
    return this.httpService.sendGetRequest({
      url:`${environment.frontendBackUrl}/noti/core/api/v1/total-count`,
      headers:new HttpHeaders({
        'x-api-key':environment.forntendApikey
      }),
    })
  }
  
  getDepartmentCumlative(){
    return this.httpService.sendGetRequest({
      url:`${environment.API_BASE_URL}/publicdashboard/departmentcumulative`,
      headers:getAuthHeaders(),
    })
  }

  stateWiseCount(){
    return this.httpService.sendGetRequest({
      url:`${environment.frontendBackUrl}/noti/core/api/v1/state-count?lang=${this.langHandlerService.currentLang}`,
      headers:new HttpHeaders({
        'x-api-key':environment.forntendApikey
      }),
    })
  }

  stateWiseUser(){
    return this.httpService.sendGetRequest({
      url:`${environment.API_BASE_URL}/publicdashboard/statewisestats`,
      headers:getAuthHeaders(),
    })
  }

  getstateWiseDeptServstats(){
    return this.httpService.sendGetRequest({
      url:`${environment.API_BASE_URL}/publicdashboard/statewisedeptservstats`,
      headers:getAuthHeaders(),
    })
  }

  fetchDBTSchemeList(payload: DBTScheme) {
    return this.httpService.sendPostRequest<any, typeof payload>({
        url: environment.CORE_API_URL + '/openschemelist',
        headers: getAuthHeaders(),
        body: payload,
      });
  }

  getUserCountsData() {
    
    return this.httpService.sendGetRequest({
        url: 'https://apigw.umang.gov.in/vc/core/api/v1/visited-count/count?entity_type=landing&mode=web&u=1',
        headers: getAuthHeaders(true),
      })
  }

  getImportantUpdates() {
    return this.httpService.sendGetRequest({
      url:'https://apigw.umang.gov.in/dept/core/api/dept/v1/important-update?mode=web',
      headers:getImportantUpdatesHeaders()
    })
  }

  getEntityCount() {
    return this.httpService.sendGetRequest({
      url:'https://apigw.umang.gov.in/noti/core/api/v1/entity-count?entity_type=service',
      headers:getAuthHeaders(true)
    })
  }  


}
